import { queryOptions } from "@tanstack/react-query";

const VERTEX_SEARCH_KEYS = () => ["vertex-search"] as const;

export type VertexSearchRequest = {
  query: string;
};
export type VertexSearchResult = {
  id: string;
  htmlSnippet: string;
  link: string;
  title: string;
};
export type VertexSearchResponse = {
  data: {
    searchResults: VertexSearchResult[];
  };
};
export type VertexSearchErrorResponse = {
  status?: {
    transactionStatus: "error";
  };
  error?: {
    cbErrCode: string;
    message: string;
    timestamp: string;
    endpoint: string;
    statusCode: number;
  };
  version?: string;
  statusCode?: number;
  createdBy?: string;
  validationErrors?: {
    error: string;
    message: string;
  }[];
};
export type VertexSearchApiResponse =
  VertexSearchResponse | VertexSearchErrorResponse;
export class VertexSearchError extends Error {}

const isVertexSearchError = (
  response: VertexSearchApiResponse,
): response is VertexSearchErrorResponse =>
  "error" in response ||
  "validationErrors" in response ||
  (response as VertexSearchErrorResponse).status?.transactionStatus === "error";

export const getResults = async (
  request: VertexSearchRequest,
  apiUrl: string,
): Promise<VertexSearchResponse> => {
  const url = `${apiUrl}?query=${encodeURIComponent(request.query)}`;

  const response = await fetch(url);

  if (!response.ok) {
    throw new VertexSearchError(
      `HTTP ${response.status.toString()}: ${response.statusText}`,
    );
  }

  const vertexSearchApiResponse =
    (await response.json()) as VertexSearchApiResponse;

  if (isVertexSearchError(vertexSearchApiResponse)) {
    const errorMessage =
      vertexSearchApiResponse.error?.message ??
      vertexSearchApiResponse.validationErrors?.[0]?.message ??
      "Unknown vertex search error";
    throw new VertexSearchError(errorMessage);
  }

  return { data: vertexSearchApiResponse.data };
};
export const resultsOptions = (request: VertexSearchRequest, apiUrl: string) =>
  queryOptions({
    queryFn: () => getResults(request, apiUrl),
    queryKey: [
      ...VERTEX_SEARCH_KEYS(),
      "veretx-search-results",
      request.query,
    ] as const,
  });
